import { useCallback, useContext, useEffect, useState } from 'react';

// Hooks
import useFetchIntervalData from '../../../../packages/hooks/useFetchIntervalData';
import useAbortController from '../../../../packages/hooks/useAbortController';

// Contexts
import FootballApiContext from '../contexts/FootballApiContext';

// Types
import { ITeamConfig, ITeamResponseData, ITeamResponse, ISportEntity, IOddsV2 } from '../../../../packages/types';
import { IGeneralSubscriberInfo } from '../../../../packages/constants/subscribeTypes';
import useSubscriptionClick from '../../../../packages/hooks/useSubscriptionClick';
import { stackOdds } from '@sportal365/smp-widget-utils/src/oddsCounter';

interface IUseTeamArgs extends IGeneralSubscriberInfo {
  dataRefreshTime?: ITeamConfig['dataRefreshTime'];
  dataTeam?: ITeamConfig['dataTeam'];
  dataSeason?: ITeamConfig['dataSeason'];
  dataMatchId?: ITeamConfig['dataMatchId'];
  teamToCompareWith?: ISportEntity;
  dataOddsDisplay?: ITeamConfig['dataOddsDisplay'];
  dataOddsBettingId?: ITeamConfig['dataOddsBettingId'];
  dataOddsMarketValueType?: ITeamConfig['dataOddsMarketValueType'];
  dataOddsPreEventOnly?: ITeamConfig['dataOddsPreEventOnly'];
  dataOddsMarket?: ITeamConfig['dataOddsMarket'];
}

export default function useTeam({
  subscribers,
  dataWidgetId,
  dataWidgetSport,
  dataRefreshTime,
  dataTeam,
  dataSeason,
  dataMatchId,
  teamToCompareWith,
  dataOddsDisplay,
  dataOddsBettingId,
  dataOddsPreEventOnly,
  dataOddsMarketValueType,
  dataOddsMarket,
  dataWidgetCuid,
}: IUseTeamArgs) {
  const [team, setTeam] = useState<ITeamResponse[]>();
  const { $team } = useContext(FootballApiContext);
  const [odds, setOdds] = useState<IOddsV2>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [getSignal] = useAbortController();

  const fetchTeam = useCallback(
    (manageLoader: boolean, isFromInterval = false) => {
      if (dataTeam && dataSeason) {
        const signal = getSignal();
        manageLoader && setIsLoading(true);

        $team
          ?.getTeam({
            signal,
            dataTeam,
            dataSeason,
            dataMatchId,
            teamToCompareWith,
            dataOddsDisplay,
            dataOddsBettingId,
            dataOddsMarket,
            dataOddsMarketValueType,
            dataOddsPreEventOnly,
          })
          ?.then((res: ITeamResponseData) => {
            manageLoader && setIsLoading(false);
            dataOddsDisplay && setOdds(res.teamStatistics[0].matchData?.odds);
            setTeam(res.teamStatistics);
            !isFromInterval &&
              dataMatchId &&
              stackOdds(res?.teamStatistics[0]?.matchData, dataWidgetSport, dataWidgetId, dataWidgetCuid, dataOddsBettingId, true);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    },
    [
      $team,
      dataMatchId,
      dataOddsDisplay,
      dataOddsBettingId,
      dataOddsMarket,
      dataOddsMarketValueType,
      dataOddsPreEventOnly,
      dataSeason,
      dataTeam,
      getSignal,
      teamToCompareWith,
      dataWidgetCuid,
      dataWidgetId,
      dataWidgetSport,
    ],
  );

  const refetchWidgetData = useFetchIntervalData(fetchTeam, dataRefreshTime);

  useEffect(() => {
    fetchTeam(true);
  }, [fetchTeam]);

  return {
    showWidget: true,
    widgetData: { team, odds, isLoading },
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
    refetchWidgetData,
  };
}
